import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';

const serviceLinks = [
  { name: 'Trademark Registration', slug: '/services/trademark-registration' },
  { name: 'Copyright Registration', slug: '/services/copyright-registration' },
  { name: 'Company Formation', slug: '/services/company-formation' },
  { name: 'Trademark Search', slug: '/services/trademark-search' },
  { name: 'Amazon Brand Registry', slug: '/services/amazon-brand-registry' },
];

const Footer = ({ onOpenContactForm }) => {
  const currentYear = new Date().getFullYear();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer data-testid="main-footer" className="text-gray-400 relative overflow-hidden">
      {/* Giant CTA with background image */}
      <div className="relative">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1650803321892-efba59b28a60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmF2eSUyMGJsdWUlMjBzbW9vdGglMjBncmFkaWVudCUyMGJhY2tncm91bmQlMjBtaW5pbWFsfGVufDB8fHx8MTc3NTUxMDcxM3ww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0F1C]/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-24 pb-16 md:pt-32 md:pb-20 relative z-10 text-center">
        <h2 className="text-[10vw] md:text-[7vw] lg:text-[6vw] font-bold tracking-tighter leading-[0.95] text-white mb-8">
          Let's Protect<br />Your Brand.
        </h2>
        <Button
          data-testid="footer-cta-button"
          onClick={onOpenContactForm}
          className="bg-[#002FA7] hover:bg-[#002280] text-white font-medium px-8 py-5 rounded-none transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,47,167,0.4)]"
        >
          Get Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
        </div>
      </div>

      <div className="border-t border-gray-800 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div>
              <span className="text-lg font-bold tracking-tight text-white mb-6 block">
                Trademark <span className="text-[#002FA7]">Logo Design</span>
              </span>
              <p className="text-sm leading-relaxed text-gray-500">
                Your dedicated partner for US trademark registration, copyright protection, company formation, and Amazon Brand Registry enrollment.
              </p>
            </div>

            <div>
              <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500 mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {['services', 'process', 'pricing', 'testimonials', 'faq'].map(id => (
                  <li key={id}>
                    <button data-testid={`footer-link-${id}`} onClick={() => scrollTo(id)} className="text-sm text-gray-500 hover:text-white transition-colors capitalize hover-underline">
                      {id}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500 mb-6">Services</h4>
              <ul className="space-y-3">
                {serviceLinks.map(s => (
                  <li key={s.slug}>
                    <Link to={s.slug} className="text-sm text-gray-500 hover:text-white transition-colors flex items-center gap-1 group">
                      {s.name}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500 mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <a href="tel:+18005551234" className="text-sm text-white hover:text-[#002FA7] transition-colors">(800) 555-1234</a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <a href="mailto:info@trademarklogodesign.com" className="text-sm text-white hover:text-[#002FA7] transition-colors">info@trademarklogodesign.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-white">9350 Wilshire Blvd Suite 203, Beverly Hills, CA 90212</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-600">&copy; {currentYear} Trademark Logo Design. All rights reserved.</p>
          <div className="flex gap-8 text-xs">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-gray-600 hover:text-white transition-colors hover-underline">Privacy Policy</Link>
            <Link to="/terms-conditions" data-testid="footer-terms-link" className="text-gray-600 hover:text-white transition-colors hover-underline">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
